<script lang="ts">
	/**
	 * Superfície interna (§4). É um ecrã de preparação com duas saídas:
	 * "Assinar agora" (fluxo A) ou "Enviar para assinatura" (fluxo B).
	 *
	 * A preparação é idêntica nos dois fluxos — muda só onde e quando se assina
	 * (§3). Por isso o formulário é um só, e a bifurcação acontece no fim.
	 */

	import type { ContextoInternoResposta, Idioma } from '@ac/shared';
	import { textos } from './textos';
	import PadAssinatura from '@ac/ui/PadAssinatura.svelte';
	import { api, ErroDoServidor } from './api';

	type Fase = 'a-carregar' | 'preparacao' | 'assinatura' | 'link-gerado' | 'concluido';

	let fase = $state<Fase>('a-carregar');
	let idioma = $state<Idioma>('pt');
	const t = $derived(textos(idioma));
	let erro = $state<string | null>(null);
	let ocupado = $state(false);

	let contexto = $state<ContextoInternoResposta | null>(null);
	let selecionados = $state<Set<string>>(new Set());
	let nomeSignatario = $state('');
	let filtro = $state('');

	let sessaoId = $state<string | null>(null);
	let assinaturaPng = $state<string | null>(null);
	let linkGerado = $state<{ url: string; expiraEm: string } | null>(null);
	let resultado = $state<{ distribuidor: string; pdfHash: string }[]>([]);
	let copiado = $state(false);

	const distribuidoresVisiveis = $derived(
		(contexto?.distribuidores ?? []).filter((d) =>
			d.nome.toLowerCase().includes(filtro.trim().toLowerCase())
		)
	);

	const podePreparar = $derived(
		selecionados.size > 0 && nomeSignatario.trim().length >= 3
	);

	$effect(() => {
		carregar();
	});

	async function carregar() {
		try {
			contexto = await api.contexto();
			idioma = contexto.idioma;
			fase = 'preparacao';
		} catch (e) {
			erro = mensagem(e);
			fase = 'preparacao';
		}
	}

	function alterna(id: string) {
		const novo = new Set(selecionados);
		if (novo.has(id)) novo.delete(id);
		else novo.add(id);
		selecionados = novo;
	}

	/** Cria os N registos em `Criado`. Comum aos dois fluxos — daí o parâmetro. */
	async function preparar(seguinte: 'assinatura' | 'link'): Promise<void> {
		if (!podePreparar || ocupado) return;
		ocupado = true;
		erro = null;

		try {
			// Se já preparámos nesta visita, não duplicamos registos.
			if (!sessaoId) {
				const resposta = await api.preparar({
					distribuidorIds: [...selecionados],
					nomeSignatario: nomeSignatario.trim()
				});
				sessaoId = resposta.sessaoId;
			}

			if (seguinte === 'assinatura') {
				fase = 'assinatura';
			} else {
				linkGerado = await api.gerarLink(sessaoId);
				fase = 'link-gerado';
			}
		} catch (e) {
			erro = mensagem(e);
		} finally {
			ocupado = false;
		}
	}

	async function submeterAssinatura() {
		if (!sessaoId || !assinaturaPng || ocupado) return;
		ocupado = true;
		erro = null;

		try {
			const resposta = await api.assinar(sessaoId, assinaturaPng);
			resultado = resposta.documentos.map((d) => ({
				distribuidor: d.distribuidor,
				pdfHash: d.pdfHash
			}));
			fase = 'concluido';
		} catch (e) {
			erro = mensagem(e);
		} finally {
			ocupado = false;
		}
	}

	async function copiarLink() {
		if (!linkGerado) return;
		await navigator.clipboard.writeText(linkGerado.url);
		copiado = true;
		setTimeout(() => (copiado = false), 2000);
	}

	function mensagem(e: unknown): string {
		return e instanceof ErroDoServidor || e instanceof Error ? e.message : t.erroInesperado;
	}

	function dataPt(iso: string): string {
		const [ano, mes, dia] = iso.slice(0, 10).split('-');
		return `${dia}/${mes}/${ano}`;
	}
</script>

<main class="mx-auto max-w-3xl px-4 py-6 sm:px-6">
	<header class="mb-6">
		<h1 class="text-xl font-semibold text-slate-900">{t.titulo}</h1>
		{#if contexto}
			<p class="mt-1 text-sm text-slate-500">
				{contexto.clinica.nome}{#if contexto.clinica.nif}&nbsp;· NIF {contexto.clinica.nif}{/if}
			</p>
		{/if}
	</header>

	{#if erro}
		<div
			role="alert"
			class="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
		>
			{erro}
		</div>
	{/if}

	{#if fase === 'a-carregar'}
		<p class="py-16 text-center text-slate-400">{t.aCarregar}</p>
	{:else if fase === 'preparacao' && contexto}
		<div class="space-y-5">
			<!-- Distribuidores -->
			<section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
				<div class="mb-3 flex items-baseline justify-between gap-3">
					<h2 class="font-medium text-slate-900">{t.distribuidores}</h2>
					<span class="text-sm text-slate-500">{selecionados.size} selecionado(s)</span>
				</div>

				<input
					type="search"
					bind:value={filtro}
					placeholder={t.procurar}
					class="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2.5
					       focus:border-hfx-500 focus:ring-2 focus:ring-hfx-200 focus:outline-none"
				/>

				<div class="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-100">
					{#each distribuidoresVisiveis as distribuidor (distribuidor.id)}
						<label
							class="flex cursor-pointer items-center gap-3 px-3 py-3
							       hover:bg-slate-50 active:bg-slate-100"
						>
							<input
								type="checkbox"
								checked={selecionados.has(distribuidor.id)}
								onchange={() => alterna(distribuidor.id)}
								class="size-5 shrink-0 rounded border-slate-300 text-hfx-500 focus:ring-hfx-400"
							/>
							<span class="min-w-0 flex-1">
								<span class="block truncate text-slate-900">{distribuidor.nome}</span>
								{#if !distribuidor.email}
									<!-- §11.3: sem email, o passo de envio (§12) fica bloqueado. -->
									<span class="text-xs text-amber-600">{t.semEmail}</span>
								{/if}
							</span>
						</label>
					{:else}
						<p class="px-3 py-8 text-center text-sm text-slate-400">
							{filtro ? t.semCorrespondencia : t.semDistribuidores}
						</p>
					{/each}
				</div>
			</section>

			<!-- Signatário e prazo -->
			<section class="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2">
				<label class="block">
					<span class="mb-1.5 block text-sm font-medium text-slate-700">{t.nomeSignatario}</span>
					<input
						type="text"
						bind:value={nomeSignatario}
						placeholder={t.nomeCompleto}
						autocomplete="off"
						class="w-full rounded-lg border border-slate-300 px-3 py-2.5
						       focus:border-hfx-500 focus:ring-2 focus:ring-hfx-200 focus:outline-none"
					/>
				</label>

			</section>

			<!-- As duas saídas (§3) -->
			<div class="flex flex-col gap-3 sm:flex-row">
				<button
					type="button"
					onclick={() => preparar('assinatura')}
					disabled={!podePreparar || ocupado}
					class="flex-1 rounded-lg bg-hfx-500 px-4 py-3.5 font-medium text-white
					       disabled:cursor-not-allowed disabled:opacity-40 hover:bg-hfx-600"
				>
					{ocupado ? t.aPreparar : t.assinarAgora}
				</button>
				<button
					type="button"
					onclick={() => preparar('link')}
					disabled={!podePreparar || ocupado}
					class="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3.5
					       font-medium text-slate-900 disabled:cursor-not-allowed disabled:opacity-40
					       hover:bg-slate-50"
				>
					{t.enviarParaAssinatura}
				</button>
			</div>
		</div>
	{:else if fase === 'assinatura' && contexto}
		<div class="space-y-5">
			<section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
				<h2 class="mb-2 font-medium text-slate-900">{t.confirme}</h2>
				<dl class="space-y-1.5 text-sm">
					<div class="flex gap-2">
						<dt class="w-32 shrink-0 text-slate-500">{t.signatario}</dt>
						<dd class="text-slate-900">{nomeSignatario}</dd>
					</div>
					<div class="flex gap-2">
						<dt class="w-32 shrink-0 text-slate-500">{t.rotuloDistribuidores}</dt>
						<dd class="text-slate-900">
							{contexto.distribuidores
								.filter((d) => selecionados.has(d.id))
								.map((d) => d.nome)
								.join(', ')}
						</dd>
					</div>
				</dl>
				<p class="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
					Gera-se uma autorização distinta por distribuidor, cada uma nomeando apenas o seu.
				</p>
			</section>

			<section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
				<h2 class="mb-3 font-medium text-slate-900">{t.assinaturaClinica}</h2>
				<PadAssinatura onchange={(png) => (assinaturaPng = png)} desativado={ocupado} {idioma} />
			</section>

			<div class="flex flex-col gap-3 sm:flex-row-reverse">
				<button
					type="button"
					onclick={submeterAssinatura}
					disabled={!assinaturaPng || ocupado}
					class="flex-1 rounded-lg bg-hfx-500 px-4 py-3.5 font-medium text-white
					       disabled:cursor-not-allowed disabled:opacity-40 hover:bg-hfx-600"
				>
					{ocupado ? t.aSelar : t.confirmarAssinatura}
				</button>
				<button
					type="button"
					onclick={() => (fase = 'preparacao')}
					disabled={ocupado}
					class="rounded-lg border border-slate-300 bg-white px-4 py-3.5 font-medium
					       text-slate-700 disabled:opacity-40 hover:bg-slate-50 sm:flex-none"
				>
					{t.voltar}
				</button>
			</div>
		</div>
	{:else if fase === 'link-gerado' && linkGerado}
		<div class="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
			<h2 class="font-medium text-slate-900">{t.linkCriado}</h2>
			<p class="text-sm text-slate-600">
				{t.linkNota(dataPt(linkGerado.expiraEm))}
			</p>

			<div class="flex gap-2">
				<input
					readonly
					value={linkGerado.url}
					class="min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5
					       font-mono text-xs text-slate-700"
				/>
				<button
					type="button"
					onclick={copiarLink}
					class="shrink-0 rounded-lg bg-hfx-500 px-4 py-2.5 text-sm font-medium text-white
					       hover:bg-hfx-600"
				>
					{copiado ? t.copiado : t.copiar}
				</button>
			</div>

			<p class="text-xs text-slate-400">
				{t.canalPorDefinir}
			</p>
		</div>
	{:else if fase === 'concluido'}
		<div class="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
			<h2 class="font-medium text-emerald-900">{t.assinado}</h2>
			<p class="text-sm text-emerald-800">
				{t.seladas(resultado.length)}
			</p>
			<ul class="space-y-2">
				{#each resultado as documento (documento.pdfHash)}
					<li class="rounded-lg bg-white px-3 py-2 text-sm">
						<span class="block text-slate-900">{documento.distribuidor}</span>
						<span class="block font-mono text-xs break-all text-slate-400">
							SHA-256 {documento.pdfHash.slice(0, 32)}…
						</span>
					</li>
				{/each}
			</ul>
			<p class="text-xs text-emerald-700">
				{t.envioBackoffice}
			</p>
		</div>
	{/if}
</main>
